import { useState, useEffect, useRef } from 'react';

/**
 * Хук для плавной анимации числа от старого значения к новому
 * @param {number} targetValue - Целевое значение
 * @param {Object} options - Опции анимации
 * @param {number} options.duration - Длительность анимации в мс (по умолчанию 1000)
 * @param {number} options.delay - Задержка перед началом анимации в мс (по умолчанию 0)
 * @param {Function} options.easing - Функция easing (по умолчанию easeOutCubic)
 * @param {Function} options.formatValue - Функция форматирования (по умолчанию Math.round)
 * @returns {number} - Текущее анимированное значение
 */
export const useAnimatedNumber = (targetValue, options = {}) => {
  const {
    duration = 1000,
    delay = 0,
    easing = (t) => 1 - Math.pow(1 - t, 3), // easeOutCubic
    formatValue = (val) => Math.round(val)
  } = options;

  const [displayValue, setDisplayValue] = useState(() => {
    // Инициализируем с отформатированным значением
    return formatValue(targetValue);
  });
  
  // Для интерполяции используем сырые числовые значения
  const startValueRef = useRef(targetValue); // Начальное сырое значение для анимации
  const currentRawValueRef = useRef(targetValue); // Текущее сырое значение во время анимации
  const startTimeRef = useRef(null);
  const delayStartTimeRef = useRef(null);
  const animationFrameRef = useRef(null);
  const targetValueRef = useRef(targetValue);

  useEffect(() => {
    // Если значение не изменилось, ничего не делаем
    if (targetValue === targetValueRef.current) {
      return;
    }

    // Останавливаем предыдущую анимацию
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }

    // Запоминаем текущее сырое значение как стартовое для новой анимации
    startValueRef.current = currentRawValueRef.current;
    targetValueRef.current = targetValue;
    startTimeRef.current = null;
    delayStartTimeRef.current = null; // Сбрасываем задержку для новой анимации

    const animate = (currentTime) => {
      // Если есть задержка, ждем её
      if (delay > 0 && delayStartTimeRef.current === null) {
        delayStartTimeRef.current = currentTime;
      }
      
      if (delay > 0 && delayStartTimeRef.current !== null) {
        const elapsedSinceDelay = currentTime - delayStartTimeRef.current;
        if (elapsedSinceDelay < delay) {
          // Пока не прошла задержка, продолжаем ждать
          animationFrameRef.current = requestAnimationFrame(animate);
          return;
        }
        // Задержка прошла, начинаем анимацию
        if (startTimeRef.current === null) {
          startTimeRef.current = currentTime;
        }
      } else if (startTimeRef.current === null) {
        startTimeRef.current = currentTime;
      }

      const elapsed = currentTime - startTimeRef.current;
      const progress = Math.min(elapsed / duration, 1);
      const easedProgress = easing(progress);

      // Интерполируем сырые числовые значения
      const currentRawValue = startValueRef.current + 
        (targetValue - startValueRef.current) * easedProgress;
      
      currentRawValueRef.current = currentRawValue;
      const formatted = formatValue(currentRawValue);
      setDisplayValue(formatted);

      if (progress < 1) {
        animationFrameRef.current = requestAnimationFrame(animate);
      } else {
        // Гарантируем точное целевое значение в конце
        currentRawValueRef.current = targetValue;
        const finalValue = formatValue(targetValue);
        setDisplayValue(finalValue);
      }
    };

    animationFrameRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetValue, duration, delay]);

  return displayValue;
};

